// apps/client/src/modules/Shortcuts/useShortcut.ts
import { useEffect, useRef } from 'react'
import { useSetAtom, useStore } from 'jotai'
import {
  shortcutRegistryAtom,
  type ShortcutDefinition,
} from './ShortcutRegistry'

/**
 * Registers a keyboard shortcut into the global registry on mount and removes
 * it on unmount. The handler is captured in a ref so the matcher always sees
 * the latest closure even if the definition object is referentially stable.
 *
 * When two non-displayOnly shortcuts share the same key combo, the matcher
 * dispatches the first one by registry insertion order — in dev we warn so
 * collisions get caught early.
 */
export const useShortcut = (def: ShortcutDefinition): void => {
  const setRegistry = useSetAtom(shortcutRegistryAtom)
  const store = useStore()
  const handlerRef = useRef(def.handler)

  // Keep the handler ref fresh every render — definitions are usually
  // inlined so handler closures change between renders.
  handlerRef.current = def.handler

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production' && !def.displayOnly) {
      // Read the registry directly from the store at effect-execution time so
      // we see entries committed by sibling effects earlier in the same flush.
      const registry = store.get(shortcutRegistryAtom)
      const bindings = Array.isArray(def.keys) ? def.keys : [def.keys]
      for (const entry of Object.values(registry)) {
        if (entry.id === def.id || entry.displayOnly) continue
        const entryBindings = Array.isArray(entry.keys) ? entry.keys : [entry.keys]
        const collides = bindings.some((a) =>
          entryBindings.some(
            (b) =>
              !!a.cmd === !!b.cmd &&
              !!a.shift === !!b.shift &&
              !!a.alt === !!b.alt &&
              a.key.toUpperCase() === b.key.toUpperCase(),
          ),
        )
        if (collides) {
          console.warn(
            `[useShortcut] Duplicate key combo registered: "${def.id}" and "${entry.id}". Only one will fire.`,
          )
        }
      }
    }

    const wrapped: ShortcutDefinition = {
      ...def,
      handler: def.handler
        ? (e: KeyboardEvent) => handlerRef.current?.(e)
        : undefined,
    }

    setRegistry((prev) => ({ ...prev, [def.id]: wrapped }))
    return () => {
      setRegistry((prev) => {
        const next = { ...prev }
        delete next[def.id]
        return next
      })
    }
    // We intentionally exclude `def.handler` from deps; the ref keeps it fresh.
  }, [
    def.id,
    def.group,
    def.labelKey,
    def.firesInEditor,
    def.displayOnly,
    JSON.stringify(def.keys),
    setRegistry,
    store,
  ])
}
